import React, { useEffect, useMemo, useRef, useState } from "react";
import "./styles.css";
import {
    getBasePrice,
    getFirstPayment,
    getInstallmentPlans,
    getLowestOption,
} from "./utils.ts";
import { SelectInstallmentOption } from "../components/SelectInstallmentOption";
import { Input } from "../../../Input";
import { RootState } from "../../../../../store";
import { connect } from "react-redux";
import {
    selectPricesByFirstPaymentData,
    selectRequiredPayment,
    selectCommercePricesByFirstPaymentData,
    selectCommerceRequiredPayment,
} from "../../../../../store/selectors.ts";
import {
    getAreas,
    getFlatViewTypes,
    getFloorRanges,
    getInstallmentPeriods,
} from "../utils.ts";
import { InfoRow } from "../../../InfoRow";
import { MANUAL_AREA } from "../../constants";
import { Switcher } from "../../../Switcher";

type PricingType = "residential" | "commerce";

type OwnProps = {
    type?: PricingType;
    housing: any;
    setShareData: (v: any) => void;
};

type StateProps = {
    pricesByFirstPayment: any;
    requiredPayment: number | null | undefined;
};

const MATCAP_1 = 728_900;
const MATCAP_2 = 963_200;

type MatCapOptionId = "none" | "child1" | "child2" | "manual";

const hasMatCapOverride = (areaStr: string) => areaStr.includes("|m]");
const isMatCapAllowedForLayout = (globalMatCap: boolean, areaStr: string) => {
    const hasM = hasMatCapOverride(areaStr);
    return globalMatCap !== hasM;
};

const getInstallmentOptionThreshold = (
    option: any,
    requiredPayment: number | null | undefined,
    areaValue: number,
    flatViewType: any,
    termPercent: number,
    markupAfterFirstPayment: boolean,
    isInstallment: boolean
) => {
    const flatTypeId = flatViewType?.value?.id;
    const baseWorth =
        flatTypeId != null ? Number(option?.value?.prices?.[flatTypeId] || 0) : 0;

    const shouldApplyTermMarkupToFirstPaymentBase =
        isInstallment && termPercent > 0 && !markupAfterFirstPayment;

    const worthForFirstPayment = shouldApplyTermMarkupToFirstPaymentBase
        ? baseWorth * (1 + termPercent / 100)
        : baseWorth;

    const optionForFirstPaymentCalc =
        flatTypeId != null
            ? {
                ...option,
                value: {
                    ...option.value,
                    prices: {
                        ...(option.value?.prices || {}),
                        [flatTypeId]: worthForFirstPayment,
                    },
                },
            }
            : option;

    return Math.max(
        Number(
            getFirstPayment(
                optionForFirstPaymentCalc,
                requiredPayment,
                areaValue,
                flatViewType
            )
        ) || 0,
        0
    );
};

const getMatchedInstallmentOption = (
    options: any,
    firstPaymentAmount: number,
    requiredPayment: number | null | undefined,
    areaValue: number,
    flatViewType: any,
    termPercent: number,
    markupAfterFirstPayment: boolean,
    isInstallment: boolean
) => {
    const normalizedOptions = Object.values(options || {}).filter(Boolean) as any[];

    if (!normalizedOptions.length) return null;

    const sortedOptions = normalizedOptions
        .map((option) => ({
            option,
            threshold: getInstallmentOptionThreshold(
                option,
                requiredPayment,
                areaValue,
                flatViewType,
                termPercent,
                markupAfterFirstPayment,
                isInstallment
            ),
        }))
        .sort((a, b) => Number(a.threshold) - Number(b.threshold));

    if (!sortedOptions.length) return null;

    return (
        sortedOptions.reduce((matched, current) => {
            if (firstPaymentAmount >= Number(current.threshold)) {
                return current.option;
            }
            return matched;
        }, sortedOptions[0].option) || null
    );
};

const getRequiredPaymentThreshold = (
    requiredPayment: number | null | undefined,
    areaValue: number
) => Math.max((Number(requiredPayment) || 0) * areaValue, 0);

export const _ByFirstPaymentLegacy: React.FC<OwnProps & StateProps> = ({
    pricesByFirstPayment,
    requiredPayment,
    setShareData,
    type = "residential",
    housing,
}) => {
    const typeLabel = type === "commerce" ? "тип помещения" : "тип квартиры";
    const selectTypeLabel = `выберите ${typeLabel}`;

    const [area, setArea] = useState<any>(0);
    const [installmentPeriod, setInstallmentPeriod] = useState<any>(0);
    const [areaWorth, setAreaWorth] = useState<number>(0);
    const [firstPaymentCash, setFirstPaymentCash] = useState<number>(0);
    const [matCapOption, setMatCapOption] = useState<any>({
        name: "без маткапа",
        value: { id: "none" as MatCapOptionId },
    });
    const [matCapManualAmount, setMatCapManualAmount] = useState<number>(0);
    const [fullPrice, setFullPrice] = useState<number>(0);
    const [rest, setRest] = useState<number>(0);
    const [monthlyPayment, setMonthlyPayment] = useState<number>(0);
    const [markup, setMarkup] = useState<number>(0);
    const [installmentOption, setInstallmentOption] = useState<any>();
    const [flatViewType, setFlatViewType] = useState<any>();
    const [floorRange, setFloorRange] = useState<any>();
    const [fullPaymentPrice, setFullPaymentPrice] = useState<number>(0);
    const [markupAfterFirstPayment, setMarkupAfterFirstPayment] =
        useState<boolean>(false);
    const shouldRecalculatePlanRef = useRef(false);
    const shouldSyncFirstPaymentWithPlanRef = useRef(true);

    const floorRanges = getFloorRanges(pricesByFirstPayment);
    const flatViewTypes = getFlatViewTypes(floorRange);
    const installmentPeriods = getInstallmentPeriods(floorRange);
    const installmentPlans = getInstallmentPlans(
        floorRange,
        installmentPeriod?.value
    );
    const areas = getAreas(pricesByFirstPayment, flatViewType);
    const isAreaSelected = Number(area?.value || 0) > 0;

    const isInstallment = !!installmentPeriod && installmentPeriod.value !== "0";
    const isManualAreaEmpty =
        area?.name === MANUAL_AREA && String(area?.value ?? "").trim().length === 0;
    const months = Number(installmentPeriod?.value || 0) || 0;
    const termPercent = Number(installmentPeriod?.periodPercent || 0) || 0;

    const matCapGloballyPossible = Boolean(
        (pricesByFirstPayment as any)?.matCap ?? housing?.matCap
    );

    const selectedAreaRaw: string = useMemo(() => {
        if (!area?.value) return "";
        if (area?.name === MANUAL_AREA) return "";

        const areaValue = String(area.value).trim();

        const allAreas: string[] = [
            ...((pricesByFirstPayment as any)?.areas || []),
            ...((pricesByFirstPayment as any)?.areas0 || []),
            ...((pricesByFirstPayment as any)?.areas2 || []),
            ...((pricesByFirstPayment as any)?.areas3 || []),
        ].filter(Boolean);

        const match = allAreas.find((a) => String(a).includes(`|${areaValue}|`));
        return (match || "").trim();
    }, [area, pricesByFirstPayment]);

    const matCapAllowedForThisLayout = useMemo(() => {
        if (!area || area?.name === MANUAL_AREA) return matCapGloballyPossible;

        if (selectedAreaRaw) {
            return isMatCapAllowedForLayout(matCapGloballyPossible, selectedAreaRaw);
        }

        return matCapGloballyPossible;
    }, [area, matCapGloballyPossible, selectedAreaRaw]);

    const matCapOptions = useMemo(
        () => [
            { name: "без маткапа", value: { id: "none" as MatCapOptionId } },
            {
                name: `маткап на 1 ребёнка (${MATCAP_1.toLocaleString()} ₽)`,
                value: { id: "child1" as MatCapOptionId },
            },
            {
                name: `маткап на 2 детей (${MATCAP_2.toLocaleString()} ₽)`,
                value: { id: "child2" as MatCapOptionId },
            },
            { name: "маткап (вручную)", value: { id: "manual" as MatCapOptionId } },
        ],
        []
    );

    const matCapPresetAmount = useMemo(() => {
        const id: MatCapOptionId = matCapOption?.value?.id || "none";
        if (id === "child1") return MATCAP_1;
        if (id === "child2") return MATCAP_2;
        return 0;
    }, [matCapOption]);

    const matCapAmountRaw = useMemo(() => {
        const id: MatCapOptionId = matCapOption?.value?.id || "none";
        if (id === "manual") return Number(matCapManualAmount) || 0;
        return matCapPresetAmount;
    }, [matCapOption, matCapManualAmount, matCapPresetAmount]);

    const matCapAmount = useMemo(() => {
        if (!matCapAllowedForThisLayout) return 0;
        return Math.max(Number(matCapAmountRaw) || 0, 0);
    }, [matCapAmountRaw, matCapAllowedForThisLayout]);

    const handleInstallmentOptionChange = (option: any, syncFirstPayment = true) => {
        shouldSyncFirstPaymentWithPlanRef.current = syncFirstPayment;
        setInstallmentOption(option);
    };

    useEffect(() => {
        if (!matCapAllowedForThisLayout) {
            setMatCapOption({
                name: "без маткапа",
                value: { id: "none" as MatCapOptionId },
            });
            setMatCapManualAmount(0);
        }
    }, [matCapAllowedForThisLayout]);

    useEffect(() => {
        if (!installmentOption) return;

        const flatTypeId = flatViewType?.value?.id;
        const baseWorth =
            flatTypeId != null
                ? Number(installmentOption.value?.prices?.[flatTypeId] || 0)
                : 0;

        setAreaWorth(baseWorth);

        const shouldApplyTermMarkupToFirstPaymentBase =
            isInstallment && termPercent > 0 && !markupAfterFirstPayment;

        const worthForFirstPayment = shouldApplyTermMarkupToFirstPaymentBase
            ? baseWorth * (1 + termPercent / 100)
            : baseWorth;

        const optionForFirstPaymentCalc =
            flatTypeId != null
                ? {
                    ...installmentOption,
                    value: {
                        ...installmentOption.value,
                        prices: {
                            ...(installmentOption.value?.prices || {}),
                            [flatTypeId]: worthForFirstPayment,
                        },
                    },
                }
                : installmentOption;

        const minFirstPayment = getFirstPayment(
            optionForFirstPaymentCalc,
            requiredPayment,
            area?.value,
            flatViewType
        );

        if (shouldSyncFirstPaymentWithPlanRef.current) {
            setFirstPaymentCash(Math.max(Number(minFirstPayment) || 0, 0));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        installmentOption,
        flatViewType,
        installmentPeriod,
        area?.value,
        requiredPayment,
        isInstallment,
        termPercent,
        markupAfterFirstPayment,
    ]);

    useEffect(() => {
        setInstallmentPeriod(installmentPeriods?.fullPrice);
        shouldSyncFirstPaymentWithPlanRef.current = true;
        setInstallmentOption({
            name: "полная стоимость",
            value: { id: "full", prices: getBasePrice(floorRange) },
        });

        setFullPaymentPrice(0);
        setFullPrice(0);
        setArea(null);
        setFirstPaymentCash(0);
        setMatCapOption({
            name: "без маткапа",
            value: { id: "none" as MatCapOptionId },
        });
        setMatCapManualAmount(0);
        setMarkup(0);
        setRest(0);
        setMonthlyPayment(0);
        setMarkupAfterFirstPayment(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [floorRange, flatViewType]);

    useEffect(() => {
        if (installmentPeriod?.value !== "0") {
            const lowestPlan = getLowestOption(installmentPlans);
            handleInstallmentOptionChange(lowestPlan, true);
        } else {
            handleInstallmentOptionChange({
                name: "полная стоимость",
                value: { id: "full", prices: getBasePrice(floorRange) },
            }, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [installmentPeriod]);

    useEffect(() => {
        if (!!floorRange && !!area?.value) {
            const basePrice = flatViewType
                ? installmentPlans?.full?.value?.prices?.[flatViewType.value.id]
                : 0;
            const base = (basePrice || 0) * Number(area.value || 0);
            setFullPaymentPrice(base);
        } else {
            setFullPaymentPrice(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [floorRange, area, flatViewType]);

    const baseFullPrice = useMemo(() => {
        const a = Number(area?.value || 0);
        const w = Number(areaWorth || 0);
        return a > 0 && w > 0 ? a * w : 0;
    }, [area, areaWorth]);

    const minimumAllowedFirstPayment = useMemo(() => {
        if (!isInstallment) return 0;

        const areaValue = Number(area?.value || 0);
        if (!areaValue) return 0;

        return getRequiredPaymentThreshold(requiredPayment, areaValue);
    }, [isInstallment, area, requiredPayment]);

    const firstPaymentTotal = useMemo(() => {
        return Math.max(Number(firstPaymentCash) || 0, 0);
    }, [firstPaymentCash]);

    useEffect(() => {
        if (!shouldRecalculatePlanRef.current) return;
        if (!isInstallment) return;
        if (!area?.value || !flatViewType?.value?.id || !installmentOption?.value?.id) return;

        const matchedOption = getMatchedInstallmentOption(
            installmentPlans,
            firstPaymentTotal,
            requiredPayment,
            Number(area.value || 0),
            flatViewType,
            termPercent,
            markupAfterFirstPayment,
            isInstallment
        );

        shouldRecalculatePlanRef.current = false;

        if (!matchedOption) return;
        if (matchedOption.value?.id === installmentOption.value?.id) return;

        handleInstallmentOptionChange(matchedOption, false);
    }, [
        isInstallment,
        firstPaymentTotal,
        installmentPlans,
        installmentOption,
        requiredPayment,
        area,
        flatViewType,
        termPercent,
        markupAfterFirstPayment,
    ]);

    useEffect(() => {
        let total = baseFullPrice;
        let computedRest = 0;
        let termMarkup = 0;

        if (isInstallment) {
            if (termPercent > 0) {
                if (markupAfterFirstPayment) {
                    const restBase = Math.max(baseFullPrice - (firstPaymentTotal || 0), 0);
                    termMarkup = restBase * (termPercent / 100);
                    const restWithMarkup = restBase + termMarkup;
                    total = (firstPaymentTotal || 0) + restWithMarkup;
                    computedRest = restWithMarkup;
                } else {
                    termMarkup = baseFullPrice * (termPercent / 100);
                    total = baseFullPrice + termMarkup;
                    computedRest = Math.max(total - (firstPaymentTotal || 0), 0);
                }
            } else {
                termMarkup = 0;
                computedRest = Math.max(baseFullPrice - (firstPaymentTotal || 0), 0);
                total = (firstPaymentTotal || 0) + computedRest;
            }
        } else {
            termMarkup = 0;
            total = baseFullPrice;
            computedRest = 0;
        }

        setMarkup(Math.round(termMarkup));
        setFullPrice(Math.round(total));
        setRest(Math.round(computedRest));
    }, [
        baseFullPrice,
        isInstallment,
        termPercent,
        markupAfterFirstPayment,
        firstPaymentTotal,
    ]);

    const restAfterMatCap = useMemo(() => {
        if (!isInstallment) return 0;
        return Math.max((Number(rest) || 0) - (Number(matCapAmount) || 0), 0);
    }, [isInstallment, rest, matCapAmount]);

    useEffect(() => {
        if (isInstallment && months >= 2) {
            setMonthlyPayment(Math.round((restAfterMatCap || 0) / months));
        } else {
            setMonthlyPayment(0);
        }
    }, [isInstallment, months, restAfterMatCap]);

    const shareData = useMemo(
        () => ({
            floor: floorRange?.value?.floorRange,
            flatType: flatViewType?.value?.name,
            area: area?.value,
            installmentPeriod: installmentPeriod?.value,
            installmentOption: installmentOption?.value?.id,
            areaWorth,
            firstPaymentCash,
            firstPayment: firstPaymentTotal,
            matCap: {
                selected: matCapOption?.value?.id,
                amount: matCapAmount,
                allowed: matCapAllowedForThisLayout,
            },
            fullPrice,
            rest,
            restAfterMatCap,
            monthlyPayment,
            markup,
            markupAfterFirstPayment,
            fullPaymentPrice,
        }),
        [
            floorRange,
            flatViewType,
            area,
            installmentPeriod,
            installmentOption,
            areaWorth,
            firstPaymentCash,
            firstPaymentTotal,
            matCapOption,
            matCapAmount,
            matCapAllowedForThisLayout,
            fullPrice,
            rest,
            restAfterMatCap,
            monthlyPayment,
            markup,
            markupAfterFirstPayment,
            fullPaymentPrice,
        ]
    );

    useEffect(() => {
        setShareData(shareData);
    }, [shareData, setShareData]);

    const isManualMatCap = (matCapOption?.value?.id as MatCapOptionId) === "manual";

    return (
        <>
            <SelectInstallmentOption
                options={floorRanges}
                label="выберите этажи"
                placeholder="этажи"
                selectedOption={floorRange}
                setSelectedOption={setFloorRange}
            />

            {floorRange && (
                <>
                    <SelectInstallmentOption
                        options={flatViewTypes}
                        label={selectTypeLabel}
                        placeholder={typeLabel}
                        selectedOption={flatViewType}
                        setSelectedOption={setFlatViewType}
                    />

                    <SelectInstallmentOption
                        options={areas}
                        label="выберите квадратуру"
                        placeholder="квадратура"
                        selectedOption={area}
                        setSelectedOption={setArea}
                    />

                    {area && area.name === MANUAL_AREA && (
                        <Input
                            value={area.value}
                            onChange={(value) => setArea({ name: MANUAL_AREA, value })}
                            label="введите квадратуру"
                            error={isManualAreaEmpty}
                            inputMode="decimal"
                            normalizeDecimal
                            containerClassName="input_container-highlight-fade"
                        />
                    )}

                    <SelectInstallmentOption
                        options={installmentPeriods}
                        label="выберите срок рассрочки"
                        placeholder="срок рассрочки"
                        selectedOption={installmentPeriod}
                        setSelectedOption={setInstallmentPeriod}
                        disabled={!isAreaSelected}
                    />

                    {isInstallment && (
                        <SelectInstallmentOption
                            options={installmentPlans}
                            selectedOption={installmentOption}
                            setSelectedOption={(option: any) =>
                                handleInstallmentOptionChange(option, true)
                            }
                            label="процент первого взноса:"
                            disabled={!isAreaSelected}
                        />
                    )}
                </>
            )}

            <div className="flex flex-col gap-2 overflow-visible pb-4">
                {isAreaSelected && (
                    <Input
                        name="areaWorth"
                        type="number"
                        placeholder="0 ₽"
                        value={Number(areaWorth) || 0}
                        onChange={(v: number) => setAreaWorth(Number(v) || 0)}
                        label="стоимость квадрата:"
                    />
                )}

                {isInstallment && isAreaSelected && (
                    <>
                        <Input
                            name="firstPaymentCash"
                            type="number"
                            placeholder="0 ₽"
                            value={Number(firstPaymentCash) || 0}
                            onChange={(v: number) => {
                                shouldRecalculatePlanRef.current = true;
                                setFirstPaymentCash(Number(v) || 0);
                            }}
                            onBlur={() => {
                                const normalizedValue = Math.min(
                                    Math.max(
                                        Number(firstPaymentCash) || 0,
                                        minimumAllowedFirstPayment
                                    ),
                                    fullPrice || Number.MAX_SAFE_INTEGER
                                );

                                if (normalizedValue === firstPaymentCash) return;

                                shouldRecalculatePlanRef.current = true;
                                setFirstPaymentCash(normalizedValue);
                            }}
                            label="первоначальный взнос (свои деньги):"
                        />

                        <SelectInstallmentOption
                            options={matCapOptions}
                            selectedOption={matCapOption}
                            setSelectedOption={setMatCapOption}
                            label="маткап (будет вычтен из остатка):"
                            disabled={!matCapAllowedForThisLayout}
                        />

                        {!matCapAllowedForThisLayout && (
                            <div className="mt-[6px] text-[12px] text-[#666]">
                                Маткап для этой планировки недоступен.
                            </div>
                        )}

                        {matCapAllowedForThisLayout && isManualMatCap && (
                            <Input
                                name="matCapManualAmount"
                                type="number"
                                placeholder="0 ₽"
                                value={Number(matCapManualAmount) || 0}
                                onChange={(v: number) => setMatCapManualAmount(Number(v) || 0)}
                                label="маткап (вручную, сколько осталось):"
                            />
                        )}
                    </>
                )}

                {isInstallment && termPercent > 0 && (
                    <div className="mt-4 mb-2">
                        <Switcher
                            checked={markupAfterFirstPayment}
                            onChange={() => setMarkupAfterFirstPayment(!markupAfterFirstPayment)}
                            label="Прибавлять наценку по сроку после первого взноса"
                            disabled={Number(firstPaymentTotal) < 1}
                        />
                        <div className="mt-1 text-[12px] text-[#666]">
                            <div>Выкл: наценка за срок начисляется на всю сумму.</div>
                            <div>Вкл: наценка за срок начисляется только на остаток после взноса.</div>
                        </div>
                    </div>
                )}

                <InfoRow
                    title="полная стоимость"
                    value={(fullPrice || 0).toLocaleString()}
                    unit="₽"
                />

                {isInstallment && (
                    <>
                        <InfoRow title="остаток" value={(rest || 0).toLocaleString()} unit="₽" />

                        {matCapAmount > 0 && (
                            <InfoRow
                                title="остаток (после маткапа)"
                                value={(restAfterMatCap || 0).toLocaleString()}
                                unit="₽"
                            />
                        )}

                        <InfoRow
                            title="ежемесячный платеж"
                            value={(monthlyPayment || 0).toLocaleString()}
                            unit="₽"
                        />

                        {!!markup && (
                            <InfoRow
                                title="наценка (за срок)"
                                value={(markup || 0).toLocaleString()}
                                unit="₽"
                            />
                        )}
                    </>
                )}
            </div>
        </>
    );
};

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
    const t: PricingType = ownProps.type ?? "residential";

    if (t === "commerce") {
        return {
            pricesByFirstPayment: selectCommercePricesByFirstPaymentData(state),
            requiredPayment: selectCommerceRequiredPayment(state),
        };
    }

    return {
        pricesByFirstPayment: selectPricesByFirstPaymentData(state),
        requiredPayment: selectRequiredPayment(state),
    };
};

export const ByFirstPaymentLegacy = connect(
    mapStateToProps,
    null
)(_ByFirstPaymentLegacy);
